import React, { Fragment, useEffect, useMemo, useState } from "react"; // useRef kaldırıldı
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Spinner,
  Table,
} from "reactstrap";

// react table
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";

// redux toolkit
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchCouponsByCompany } from "../../../slices/apps/orderSlice";
import { fetchCouponSummary } from "../../../slices/apps/companySlice";

// sayfalar, modallar vb.
import DebouncedInput from "../../../Components/Common/DebouncedInput";
import TablePagination from "../../../Components/Common/TablePagination";

// Column Definitions
const columnHelper = createColumnHelper();

const pageSizeOptions = [10, 25, 50, 100, 200];

// Para birimi formatlayıcısı
const currencyFormatter = new Intl.NumberFormat("tr-TR", {
  style: "currency",
  currency: "TRY",
});

// Tarih ve saat formatlayıcısı
const dateTimeFormatter = (dateString) => {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleString("tr-TR");
};

const CouponTable = ({ companyId }) => {
  const dispatch = useDispatch();

  const { couponsByCompany, couponsByCompanyLoading } = useSelector((state) => {
    return {
      couponsByCompany: state.order.couponsByCompany,
      couponsByCompanyLoading: state.order.couponsByCompanyLoading,
    };
  }, shallowEqual);

  // States
  const [data, setData] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [filterUsedStatus, setFilterUsedStatus] = useState("");

  useEffect(() => {
    if (!couponsByCompany) {
      setData([]);
      return;
    }
    const filteredData = couponsByCompany?.filter((coupon) => {
      if (filterUsedStatus && coupon.isUsed.toString() !== filterUsedStatus)
        return false;

      if (globalFilter) {
        const searchTerm = globalFilter.toLocaleLowerCase("tr");
        const searchableFields = [
          coupon.couponCode,
          coupon.status,
          coupon.couponAmount,
        ];
        const match = searchableFields.some(
          (field) =>
            field &&
            field.toString().toLocaleLowerCase("tr").includes(searchTerm)
        );
        if (!match) return false;
      }
      return true;
    });
    setData(filteredData);
  }, [globalFilter, filterUsedStatus, couponsByCompany]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("couponCode", {
        header: "Kupon Kodu",
        size: 120,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.couponCode}
          </div>
        ),
      }),
      columnHelper.accessor("status", {
        header: "Durum",
        size: "auto",
        minSize: 150,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {row.original.status}
          </div>
        ),
      }),
      columnHelper.accessor("couponAmount", {
        header: "Kupon Tutarı",
        size: 150,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {currencyFormatter.format(row.original.couponAmount)}
          </div>
        ),
      }),
      columnHelper.accessor("creationDate", {
        header: "Oluşturma Tarihi",
        size: 160,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {dateTimeFormatter(row.original.creationDate)}
          </div>
        ),
      }),
      columnHelper.accessor("usedDate", {
        header: "Kullanım Tarihi",
        size: 160,
        cell: ({ row }) => (
          <div className="text-truncate fw-medium fs-12">
            {dateTimeFormatter(row.original.usedDate)}
          </div>
        ),
      }),
    ],
    []
  );

  const table = useReactTable({
    data: data,
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const { getHeaderGroups, getRowModel, setPageSize, getState } = table;

  const refreshData = async () => {
    try {
      await dispatch(fetchCouponsByCompany(companyId)).unwrap();
      await dispatch(fetchCouponSummary(companyId)).unwrap();
    } catch (error) { /* Silently handled */ }
  };

  return (
    <Fragment>
      <Card>
        <CardBody>
          <Button
            className="btn btn-label btn"
            color="warning"
            type="button"
            onClick={() => refreshData()}
          >
            <i className="ri-refresh-line label-icon align-middle me-2"></i>
            Yenile
          </Button>
        </CardBody>
      </Card>
      <Card>
        <CardHeader className="align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1 fs-14">
            Kupon Hak Ediş Listesi
          </h4>
          <div className="flex-shrink-0"></div>
        </CardHeader>
        <CardBody>
          <Row className="mb-3">
            <CardBody className="border border-dashed border-end-0 border-start-0 border-top-0">
              <form>
                <div className="d-flex flex-wrap justify-content-between">
                  <div className="d-flex flex-wrap" style={{ gap: "10px" }}>
                    <div style={{ width: "100px" }}>
                      <select
                        className="form-select mb-3"
                        aria-label="Sayfa başına kayıt sayısı"
                        value={getState().pagination.pageSize}
                        onChange={(e) => setPageSize(Number(e.target.value))}
                      >
                        {pageSizeOptions.map((size) => (
                          <option key={size} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <div
                      className="search-box me-2 mb-2 d-inline-block border"
                      style={{ borderRadius: "0.25em" }}
                    >
                      <DebouncedInput
                        value={globalFilter ?? ""}
                        onChange={(value) => setGlobalFilter(value)}
                        placeholder={"Ara..."}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </div>
              </form>
              <Row>
                <Col sm={6} md={4} lg={3} xl={2}>
                  <label className="form-label">Kullanım Durumu</label>
                  <select
                    className="form-select mb-3"
                    aria-label="Kullanım Durumu Filtresi"
                    value={filterUsedStatus}
                    onChange={(e) => {
                      setFilterUsedStatus(e.target.value);
                    }}
                  >
                    <option value="">Tümü</option>
                    <option value={true}>Kullanıldı</option>
                    <option value={false}>Kullanılmadı</option>
                  </select>
                </Col>
              </Row>
            </CardBody>
          </Row>

          <div
            style={{
              overflowX: "auto",
            }}
          >
            <Table className="align-middle">
              <thead>
                {getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="text-muted"
                        style={{
                          width: header.column.columnDef.size,
                          minWidth: header.column.columnDef.minSize,
                          maxWidth: header.column.columnDef.maxSize,
                          letterSpacing: "0.8px",
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: header.column.getCanSort()
                                ? "pointer"
                                : "default",
                              userSelect: "none",
                            }}
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                            {{
                              asc: <i className="ri-arrow-down-s-line"></i>,
                              desc: <i className="ri-arrow-up-s-line"></i>,
                            }[header.column.getIsSorted()] ?? null}
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {couponsByCompanyLoading ? (
                  <tr>
                    <td colSpan="100%" className="text-center pt-5 pb-5">
                      <Spinner color="primary" />
                      <p className="text-dark">Yükleniyor...</p>
                    </td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan="100%"
                      className="text-center pt-5 pb-5 text-muted"
                    >
                      <h1>
                        <i className="ri-error-warning-line text-muted"></i>
                      </h1>
                      <p>Kayıt bulunamadı!</p>
                    </td>
                  </tr>
                ) : (
                  getRowModel().rows.map((row) => {
                    return (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell) => {
                          return (
                            <td
                              key={cell.id}
                              style={{
                                width: cell.column.columnDef.size,
                                minWidth: cell.column.columnDef.minSize,
                                maxWidth: cell.column.columnDef.maxSize,
                              }}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          <TablePagination table={table} />
        </CardBody>
      </Card>
    </Fragment>
  );
};

export default CouponTable;
